// LB-17 Ask-to-buy (28 Sep 2026), kid side. Signed off from the boards at
// https://claude.ai/artifact/31jMR9LP1JQdABvL9Ag6fK (K1 card, K2 form, K3 choice, K4 outcomes).
//
// The problem it solves: a child who wanted something had no way to raise it in the app — the
// Till's "Ask {name} first" is parent-led and in person — so the ask happened off-app and the
// goal-impact moment was lost. Now the child names it and its price, sees what buying does to
// their money and goals on the Till's own choice screen, and either asks a grown-up, makes it
// a goal themselves, or keeps saving.
//
// The ask is an ordinary `state.requests` entry (`ask_to_buy`), which is why the Requests
// badge, the pending list, the parent push and the child's approval celebration all come
// free. ONE open ask per child (Alan, 28 Sep); the entry card resets once a grown-up decides,
// and the decision itself is a dismissable card on Home (DecisionCard). The money maths lives in lib/askToBuy.js so the parent card quotes the
// same numbers.
import { Icon, uploadResizedImage } from "../components/Common.jsx";
import { ChoiceScreen } from "../components/Till.jsx";
import { CURRENCY_SYMBOL } from "../lib/constants.js";
import { ASK_TO_BUY_TYPE, askAvailability, askImpact, askOutcome, decisionItemName, decisionRequests, gapSplit, openAsk } from "../lib/askToBuy.js";
import { fmtNumber, fmtPence, penceToTokens } from "../lib/utils.js";
import { t as ct } from "../lib/content.js";
import { imageSrc } from "../lib/origin.js";
import { ageBand, track } from "../lib/metrics.js";
const { useMemo, useState } = React;

/* --------------------------- Rewards tab card ----------------------------- */
// One block under My goals (saving stays first on the tab). Two faces (Alan, 28 Sep — the card
// resets as soon as a grown-up decides, so another ask is always one tap away):
//   no ask waiting → "Seen something you want?" + the arrow to ask
//   an ask waiting → the item and "Waiting for a grown-up to decide", no arrow
// What the grown-up decided is told once, on Home, by DecisionCard below.
export function AskToBuyCard({ child, requests, rate, adultName, onOpen }) {
  const waiting = openAsk(requests, child.id);
  const title = waiting
    ? ct("askbuy.card_waiting_title", "{what} · {price}", { what: waiting.title, price: fmtPence(waiting.pricePence) })
    : ct("askbuy.card_title", "Seen something you want?");
  const body = waiting
    ? ct("askbuy.card_waiting_body", "Waiting for a grown-up to decide")
    : ct("askbuy.card_body", "Ask a grown-up, and see what it does to your goals.");
  const discClass = "bg-amber-50 text-amber-600";
  return (
    <div className="kid-rows mt-3" data-testid="ask-to-buy-card" data-state={waiting ? "waiting" : "entry"}>
      <button className="kid-row w-full text-left" onClick={waiting ? undefined : onOpen} disabled={!!waiting} aria-label={waiting ? undefined : ct("askbuy.card_cta", "Ask to buy")} data-testid="ask-to-buy-open">
        <span className="flex items-center gap-3 min-w-0">
          <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${discClass}`} aria-hidden="true"><Icon id="ic-wish" size={22} style={{ color: "currentColor" }} /></span>
          <span className="min-w-0">
            <span className="block font-semibold text-sm text-slate-900 break-words">{title}</span>
            <span className="block text-xs text-slate-500">{body}</span>
          </span>
        </span>
        {!waiting && <Icon id="ic-forward" size={16} className="text-slate-400 flex-shrink-0" style={{ color: "currentColor" }} />}
      </button>
    </div>
  );
}

/* --------------------------- Home: the decision --------------------------- */
// Alan, 28 Sep: when a grown-up decides on a goal or a spend, the child sees a card at the top
// of Home until they close it. It covers an ask-to-buy (yes, gift, loan, made a goal, or a
// no), a goal redemption and a reward claim. A yes is attributed; a no is NEVER attributed
// (LB-346) and only an ask raises one — the goal and reward requests have always resolved
// quietly on a no, and this card does not change that.
//
// "Seen" is per device in localStorage, keyed by request id — the approval celebration's own
// pattern (ChildView), including its first-run rule: the first time a device tracks a child,
// everything already decided is marked seen silently, so an upgrade never replays history.
// One card at a time, newest first; closing it shows the next one if there is one.
export function decisionCopy(req, who) {
  const outcome = askOutcome(req);
  const what = decisionItemName(req);
  if (req.requestType === "goal_redeem") {
    return { tone: "teal", title: ct("askbuy.card_yes_title", "{who} said yes to your {what}", { who, what }), body: ct("decision.goal_body", "Your goal is done. Enjoy it.") };
  }
  if (req.requestType === "experience_redeem") {
    return { tone: "teal", title: ct("askbuy.card_yes_title", "{who} said yes to your {what}", { who, what }), body: ct("askbuy.card_yes_body", "{tokens} tokens spent. Enjoy it.", { tokens: fmtNumber(req.requestedAmount) }) };
  }
  if (outcome === "approved") return { tone: "teal", title: ct("askbuy.card_yes_title", "{who} said yes to your {what}", { who, what }), body: ct("askbuy.card_yes_body", "{tokens} tokens spent. Enjoy it.", { tokens: fmtNumber(req.requestedAmount) }) };
  if (outcome === "gift") return { tone: "teal", title: ct("askbuy.card_yes_title", "{who} said yes to your {what}", { who, what }), body: ct("askbuy.card_gift_body", "{tokens} tokens spent. {who} covered {gap} of them as a gift.", { tokens: fmtNumber(req.requestedAmount), who, gap: fmtNumber(req.gapTokens || 0) }) };
  if (outcome === "loan") return { tone: "amber", title: ct("askbuy.card_yes_title", "{who} said yes to your {what}", { who, what }), body: ct("askbuy.card_loan_body", "{who} lent you {gap} tokens. What you earn pays it back first.", { who, gap: fmtNumber(req.gapTokens || 0) }) };
  if (outcome === "goal") return { tone: "teal", title: ct("askbuy.card_goal_title", "{what} is a goal now", { what }), body: ct("askbuy.card_goal_body", "Keep saving. It\u2019s in My goals at {price}.", { price: fmtPence(req.pricePence) }) };
  // Never attributed (LB-346 principle, Alan 19 Sep): a no is "Not this time" with no name.
  return { tone: "slate", title: ct("askbuy.card_no_title", "Not this time"), body: ct("askbuy.card_no_body", "Your tokens stay where they are.") };
}

export function DecisionCard({ code, child, requests, adultName }) {
  const seenKey = `lb_decided_${code}_${child.id}`;
  const decided = decisionRequests(requests, child.id);
  const [seen, setSeen] = useState(() => readSeen(seenKey, decided));
  const next = decided.find((r) => !seen.has(r.id));
  if (!next) return null;
  const who = next.adultName || adultName || ct("askbuy.someone", "A grown-up");
  const { tone, title, body } = decisionCopy(next, who);
  const discClass = { amber: "bg-amber-50 text-amber-600", teal: "bg-teal-50 text-teal-700", slate: "bg-slate-100 text-slate-500" }[tone];
  const close = () => {
    const after = new Set(seen); after.add(next.id);
    try { localStorage.setItem(seenKey, JSON.stringify([...after])); } catch {}
    setSeen(after);
  };
  return (
    <div className="kid-rows mb-2" data-testid="decision-card" data-request={next.requestType} data-outcome={next.requestType === ASK_TO_BUY_TYPE ? askOutcome(next) : "approved"}>
      <div className="kid-row w-full">
        <span className="flex items-center gap-3 min-w-0 flex-1">
          <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${discClass}`} aria-hidden="true"><Icon id="ic-wish" size={22} style={{ color: "currentColor" }} /></span>
          <span className="min-w-0">
            <span className="block font-semibold text-sm text-slate-900 break-words">{title}</span>
            <span className="block text-xs text-slate-500">{body}</span>
          </span>
        </span>
        <button type="button" onClick={close} aria-label={ct("decision.dismiss", "Close")} data-testid="decision-close" className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-slate-100 text-slate-500">
          <Icon id="ic-close" size={14} style={{ color: "currentColor" }} />
        </button>
      </div>
    </div>
  );
}

// First run on this device for this child: everything already decided is seen.
function readSeen(key, decided) {
  let raw = null;
  try { raw = localStorage.getItem(key); } catch {}
  if (raw === null) {
    const ids = decided.map((r) => r.id);
    try { localStorage.setItem(key, JSON.stringify(ids)); } catch {}
    return new Set(ids);
  }
  try { return new Set(JSON.parse(raw)); } catch { return new Set(); }
}

/* -------------------------------- The sheet ------------------------------- */
// K2: what it is, what it costs, an optional photo (the goal uploader, 400px), with the
// child's balances in the header and the live "= N tokens · you have M" line. Next opens the
// Till's ChoiceScreen in "ask" mode. Portaled full-screen (z-50) like the choice screen.
export function AskToBuySheet({ child, code, rate, onSend, onMakeGoal, onClose }) {
  const [what, setWhat] = useState("");
  const [price, setPrice] = useState("");
  const [image, setImage] = useState(null);
  const [imageError, setImageError] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [error, setError] = useState("");
  const [choiceOpen, setChoiceOpen] = useState(false);

  const pricePence = useMemo(() => { const raw = parseFloat(price); return raw > 0 ? Math.round(raw * 100) : 0; }, [price]);
  const tokensNeeded = pricePence > 0 ? penceToTokens(pricePence, rate) : 0;
  const availability = askAvailability(child, tokensNeeded, rate);
  // Both are cheap arithmetic on the child's balances, so they recompute with the render
  // rather than memoising on a derived object.
  const split = availability.affordable ? gapSplit(child, tokensNeeded, rate) : null;
  const impact = availability.affordable ? askImpact(child, tokensNeeded, rate, split) : null;

  async function handlePhoto(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setImageError(""); setImageUploading(true);
    try { setImage(await uploadResizedImage(code, file, 400, 0.85)); }
    catch (err) { setImageError(err.message || ct("askbuy.photo_error", "Couldn\u2019t use that photo.")); }
    finally { setImageUploading(false); }
  }
  function next() {
    if (!what.trim()) { setError(ct("askbuy.error_what", "What is it?")); return; }
    if (!(pricePence > 0)) { setError(ct("askbuy.error_price", "How much does it cost?")); return; }
    setError("");
    setChoiceOpen(true);
  }
  function send() {
    const ok = onSend({ what: what.trim(), pricePence, tokens: tokensNeeded, image });
    if (ok === false) { setChoiceOpen(false); return; }
    track("ask_to_buy_sent", { band: ageBand(child) || undefined, value: pricePence, kind: availability.affordable ? "covered" : "short" });
    setChoiceOpen(false);
    onClose();
  }

  const tile = (label, value, testid) => (
    <div className="bg-white/15 rounded-lg px-3 py-2 flex-1 min-w-0" data-testid={testid}>
      <div className="text-[11px] font-bold opacity-85 truncate">{label}</div>
      <div className="text-lg font-extrabold whitespace-nowrap">{value}</div>
    </div>
  );

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 bg-white flex flex-col" role="dialog" aria-label="Ask to buy" data-testid="ask-to-buy-sheet">
      <div className="bg-teal-700 text-white px-5 pb-6 relative [&>*]:max-w-lg [&>*]:mx-auto" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 2.75rem)" }}>
        <button onClick={onClose} aria-label="Close" className="absolute top-0 right-4 mt-[calc(env(safe-area-inset-top,0px)+2.5rem)] w-9 h-9 rounded-full bg-white/20 text-white flex items-center justify-center"><Icon id="ic-close" size={16} style={{ color: "currentColor" }} /></button>
        <div className="text-2xl font-extrabold pr-12">{ct("askbuy.card_title", "Seen something you want?")}</div>
        <div className="text-sm opacity-90 mt-1">{ct("askbuy.sheet_sub", "Tell us what it is and what it costs.")}</div>
        <div className="flex gap-2.5 mt-3">
          {tile(ct("hero.tokens_label", "Tokens"), fmtNumber(child.tokenBalance), "askbuy-tile-tokens")}
          {tile(ct("hero.vault_label", "Savings Vault"), fmtPence(child.vaultBalancePence), "askbuy-tile-vault")}
          {tile(ct("askbuy.can_spend", "Can spend"), fmtNumber(availability.availableTokens), "askbuy-tile-total")}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 pt-6 pb-6 flex flex-col gap-5 w-full max-w-lg mx-auto" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)" }}>
        <div className="flex flex-col gap-2">
          <label htmlFor="askbuy-what" className="text-sm font-bold text-slate-900">{ct("askbuy.what_label", "What is it?")}</label>
          <input id="askbuy-what" className="border border-slate-200 rounded-lg px-3.5 py-3 text-base font-semibold w-full" placeholder={ct("askbuy.what_placeholder", "e.g. Lego set, football, book")} value={what} onChange={(e) => setWhat(e.target.value)} />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="askbuy-price" className="text-sm font-bold text-slate-900">{ct("askbuy.price_label", "How much does it cost?")}</label>
          <div className="flex items-center border border-slate-200 rounded-lg px-3.5 gap-1.5">
            <span className="text-xl font-extrabold text-slate-400">{CURRENCY_SYMBOL}</span>
            <input id="askbuy-price" className="py-3 text-xl font-extrabold flex-1 min-w-0 outline-none" type="number" inputMode="decimal" min="0.01" step="0.01" placeholder="0.00" value={price} onChange={(e) => setPrice(e.target.value)} />
          </div>
          {pricePence > 0 && (
            <div className="text-sm font-semibold text-slate-500" data-testid="askbuy-tokens-line">
              {availability.affordable
                ? ct("askbuy.tokens_line_covered", "= {tokens} tokens \u00B7 you have {have}, so you can cover it", { tokens: tokensNeeded, have: availability.availableTokens })
                : ct("askbuy.tokens_line_short", "= {tokens} tokens \u00B7 you have {have}, so you\u2019re {short} short", { tokens: tokensNeeded, have: availability.availableTokens, short: availability.shortfall })}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <div className="text-sm font-bold text-slate-900">{ct("askbuy.photo_label", "Add a photo")} <span className="font-semibold text-slate-400">{ct("askbuy.photo_optional", "(optional)")}</span></div>
          {image ? (
            <div className="flex items-center gap-3">
              <img src={imageSrc(image)} alt="" className="w-16 h-16 rounded-lg object-cover" />
              <label className="text-sm font-bold text-slate-900 underline cursor-pointer">{imageUploading ? "\u2026" : ct("askbuy.photo_change", "Change")}<input type="file" accept="image/*" className="hidden" disabled={imageUploading} onChange={handlePhoto} /></label>
            </div>
          ) : (
            <label className="flex items-center gap-2.5 px-3.5 py-3 border border-dashed border-slate-400 rounded-lg text-sm font-bold text-slate-900 cursor-pointer w-fit">
              <Icon id="ic-photo" size={20} style={{ color: "currentColor" }} />{imageUploading ? "\u2026" : ct("askbuy.photo_cta", "Take or choose a photo")}
              <input type="file" accept="image/*" className="hidden" disabled={imageUploading} onChange={handlePhoto} />
            </label>
          )}
          {imageError && <div className="text-rose-600 text-xs font-semibold">{imageError}</div>}
        </div>
        {error && <div className="text-rose-600 text-sm font-semibold" data-testid="askbuy-error">{error}</div>}
        <div className="flex-1" />
        <button className="w-full min-h-[52px] rounded-lg bg-slate-900 text-white text-base font-extrabold" onClick={next} data-testid="askbuy-next">{ct("askbuy.next", "Next")}</button>
      </div>
      {choiceOpen && (
        <ChoiceScreen
          child={child}
          what={what.trim()}
          pricePence={pricePence}
          tokensNeeded={tokensNeeded}
          impact={impact}
          availability={availability}
          rate={rate}
          mode="ask"
          onBuy={send}
          onGoal={() => { onMakeGoal({ title: what.trim(), targetPence: pricePence, image: image || null }); track("ask_to_buy_goal", { band: ageBand(child) || undefined, kind: "kid" }); }}
          onSave={() => { setChoiceOpen(false); onClose(); }}
          onBack={() => setChoiceOpen(false)}
        />
      )}
    </div>,
    document.body
  );
}
